/**
 * PDP short-description truncation (COM-180).
 *
 * Port of SFRA `truncateShortDescription`
 * (`org_hec/cartridge/models/product/decorators/description.js:3-13`, used by
 * FSA, HSA and WDH alike): over 225 characters, cut at the last full stop
 * inside the first 225 and append `..`; with no full stop, cut at 225 and
 * append `...`. The template then renders the result followed by a "Read More"
 * link to the long description.
 *
 * SFRA counts and cuts the raw MARKUP string — tags included — so the same
 * limit applies here and the cut lands where SFRA's does for plain-text
 * descriptions (the common case). Unlike SFRA it never emits broken HTML:
 *  - HTML comments (Word's `<!--[if gte mso 9]>…<![endif]-->` included) are
 *    dropped first — they never render, and a cut inside one would leave it
 *    open and swallow everything after it,
 *  - a cut that would land inside a `<tag …>` backs off to before it — and a
 *    `>` inside a quoted attribute value (`title="5 > 3"`) does not end the
 *    tag, so the cut can never leave a quote open,
 *  - a `.` inside a tag (e.g. an `href`) is not taken as a sentence end,
 *  - a trailing half entity (`&am`) is dropped,
 *  - tags left open by the cut are closed — except a `<p>` that a later `<p>`
 *    or block element already closed implicitly (a stray `</p>` is an empty
 *    paragraph to the browser),
 *  - a `<` that cannot open a tag (`Contains < 5mg`) is text, as it is to
 *    the browser — it neither hides the copy after it nor its full stops.
 * SFRA relies on the browser to repair those; we hand the result to
 * `dangerouslySetInnerHTML`, where an unclosed tag would swallow the
 * "Read More" link that follows it.
 */
import { sanitizeHtml } from "~/utils/sanitize-html";

import type { ShortDescriptionSource } from "./types";

/** SFRA's limit, in characters of markup. */
export const SHORT_DESCRIPTION_LIMIT = 225;

const VOID_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "source",
  "track",
  "wbr",
]);

/**
 * Does the `<` at `i` open a tag? Only when followed by a letter, `/`, `!` or
 * `?` — the HTML tokenizer's rule. Any other `<` (`< 5mg`, `a<b`) is text.
 * `lookahead` is the UNcut markup, so a `<` right at the cut is judged by the
 * character after it, not by the cut.
 */
function opensTag(lookahead: string, i: number): boolean {
  return lookahead[i] === "<" && /[A-Za-z/!?]/.test(lookahead[i + 1] ?? "");
}

/**
 * Start tags that close an open `<p>` (the HTML parser's implied end tag).
 * Simplified to "any open `p`": the parser stops at table / button scope, which
 * short-description copy does not use.
 */
const CLOSES_P = new Set([
  "address",
  "article",
  "aside",
  "blockquote",
  "details",
  "div",
  "dl",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "main",
  "menu",
  "nav",
  "ol",
  "p",
  "pre",
  "section",
  "table",
  "ul",
]);

/** HTML comments, and an unterminated one running to the end. */
const COMMENTS = /<!--[\s\S]*?(?:-->|$)/g;

/**
 * One pass over `head`: the last `.` in text (not inside a tag), and the start
 * of a tag the cut leaves unterminated (-1 if none). Inside a tag a quoted
 * value runs to its closing quote, so its `>` does not end the tag.
 */
function scanHead(
  head: string,
  lookahead: string,
): { lastPeriod: number; openTag: number } {
  let lastPeriod = -1;
  let openTag = -1;
  let quote: string | null = null;
  for (let i = 0; i < head.length; i++) {
    const ch = head[i];
    if (openTag === -1) {
      if (opensTag(lookahead, i)) openTag = i;
      else if (ch === ".") lastPeriod = i;
    } else if (quote) {
      if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === ">") {
      openTag = -1;
    }
  }
  return { lastPeriod, openTag };
}

/** Append closing tags for every element the (cut) markup leaves open. */
function closeOpenTags(markup: string): string {
  const open: string[] = [];
  // Quote-aware, like `scanHead`: `title="5 > 3"` is one tag.
  const tag = /<(\/?)([a-zA-Z][\w-]*)(?:"[^"]*"|'[^']*'|[^'">])*?(\/?)>/g;
  let m: RegExpExecArray | null;
  while ((m = tag.exec(markup)) !== null) {
    const [, closing, rawName, selfClosing] = m;
    const name = rawName.toLowerCase();
    if (!closing && CLOSES_P.has(name)) {
      const at = open.lastIndexOf("p");
      if (at !== -1) open.splice(at);
    }
    if (VOID_ELEMENTS.has(name) || selfClosing) continue;
    if (closing) {
      const at = open.lastIndexOf(name);
      if (at !== -1) open.splice(at);
    } else {
      open.push(name);
    }
  }
  return (
    markup +
    open
      .reverse()
      .map(name => `</${name}>`)
      .join("")
  );
}

/** Drop every HTML comment. They never render; see `truncateShortDescription`. */
export function stripHtmlComments(markup: string): string {
  return markup.replace(COMMENTS, "");
}

export function truncateShortDescription(rawMarkup: string): string {
  const markup = stripHtmlComments(rawMarkup);
  if (markup.length <= SHORT_DESCRIPTION_LIMIT) return markup;

  let head = markup.substring(0, SHORT_DESCRIPTION_LIMIT);
  // Never end inside a tag: drop an unterminated trailing `<tag…`. A `.`
  // inside it was never a candidate, so the sentence end stands.
  const { lastPeriod, openTag } = scanHead(head, markup);
  if (openTag !== -1) head = head.substring(0, openTag);

  const sentenceEnd = lastPeriod + 1;
  const cut =
    sentenceEnd > 0
      ? head.substring(0, sentenceEnd) + ".."
      : head.replace(/&[a-zA-Z0-9#]*$/, "") + "...";

  return closeOpenTags(cut);
}

/**
 * The PDP's short-description HTML. It goes to `dangerouslySetInnerHTML`, so
 * `sanitizeHtml` is the LAST transform — nothing may rewrite markup after it.
 *
 *  1. Comments are stripped BEFORE sanitising: removing one can splice two
 *     harmless halves into a live payload (`<img src=x on<!-- -->error=…>`
 *     becomes `<img src=x onerror=…>`), and `sanitizeHtml` never removes them.
 *  2. Sanitising before truncating means the 225-char limit counts only markup
 *     that will actually render, and a `<script>` cut in half by the limit can
 *     never reach the page.
 *  3. The truncated result is sanitised again, so whatever the cut and the tag
 *     repair produce is checked before it renders.
 */
export function shortDescriptionHtml(markup: string): string {
  return sanitizeHtml(
    truncateShortDescription(sanitizeHtml(stripHtmlComments(markup))),
  );
}

/**
 * SFRA's `shortDescriptionTruncated` source (org_hec `decorators/description.js`):
 * the site short description, falling back to the catalog one.
 */
export function resolveShortDescription(
  p: ShortDescriptionSource,
): string | undefined {
  const site =
    typeof p.c_siteShortDescription === "string"
      ? p.c_siteShortDescription
      : undefined;
  const catalog =
    typeof p.shortDescription === "string" ? p.shortDescription : undefined;
  return site || catalog || undefined;
}
